import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { ProSetToggle } from '@sorare/core/src/components/navigation/AppNavigation/ProSetToggle/index.tsx';
import { UserCurrencies } from '@sorare/core/src/components/navigation/AppNavigation/UserCurrencies';
import { UserNav } from '@sorare/core/src/components/navigation/UserNav';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove.ts';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags.tsx';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery.ts';

import { SeriesBoardTrckSwitcher } from '#src/components/series/SeriesBoard/SeriesBoardTrckSwitcher/index.tsx';
import { InviteButton } from '#src/components/series/inviteFriends/InviteButton/index.tsx';

import type {
  SeriesBoardHeader_squad,
  SeriesBoardHeader_tasksTrack,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  padding: var(--unit) var(--double-unit) 0 var(--double-unit);

  @media ${desktopAndAbove} {
    padding: var(--unit) 0 0 var(--double-unit);
  }
`;

type Props = {
  trck?: SeriesBoardHeader_tasksTrack;
  squad: Nullable<SeriesBoardHeader_squad>;
  isReplay: boolean;
  switcherOpen: boolean;
  onSwitcherToggle: () => void;
};

const StyledSBHorizontal = styled(SBHorizontal)`
  &.hasProSetToggle {
    display: grid;
    grid-template-columns: 1fr max-content 1fr;
  }
`;

const InviteContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const SeriesBoardHeader = ({
  trck,
  squad,
  isReplay,
  switcherOpen,
  onSwitcherToggle,
}: Props) => {
  const { sport } = useSportContext();
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const seriesNav = useSeriesNav();
  const {
    flags: { enableInviteFriends = false, enableSetSquad = false },
  } = useFeatureFlags();

  const showUserNav = seriesNav && !isDesktopAndAbove;
  const showInviteButton = !isReplay && enableInviteFriends;

  return (
    <Root>
      {!!showUserNav && (
        <StyledSBHorizontal className="hasProSetToggle">
          <Horizontal>
            <UserNav
              sport={sport}
              size={isDesktopAndAbove ? 'medium' : 'small'}
            />
            <UserCurrencies variant="vertical" />
          </Horizontal>
          <ProSetToggle />
          {!!showInviteButton && (
            <InviteContainer>
              <InviteButton />
            </InviteContainer>
          )}
        </StyledSBHorizontal>
      )}
      {!!trck && (
        <SBHorizontal>
          <SeriesBoardTrckSwitcher
            trck={trck}
            switcherOpen={!!enableSetSquad && switcherOpen}
            onSwitcherToggle={enableSetSquad ? onSwitcherToggle : undefined}
            squad={squad}
          />
          {!showUserNav && !!showInviteButton && <InviteButton />}
        </SBHorizontal>
      )}
    </Root>
  );
};

SeriesBoardHeader.fragments = {
  tasksTrack: gql`
    fragment SeriesBoardHeader_tasksTrack on TasksTrack {
      id
      ...SeriesBoardTrckSwitcher_tasksTrack
    }
    ${SeriesBoardTrckSwitcher.fragments.tasksTrack}
  ` as TypedDocumentNode<SeriesBoardHeader_tasksTrack>,
  squad: gql`
    fragment SeriesBoardHeader_squad on Squad {
      id
      ...SeriesBoardTrckSwitcher_squad
    }
    ${SeriesBoardTrckSwitcher.fragments.squad}
  ` as TypedDocumentNode<SeriesBoardHeader_squad>,
};
